import { Grid, Stack, Text } from '@mantine/core';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import PageLoader from 'src/UI/loader/PageLoader';
import { useAppContainerContext } from 'src/UI/app-container/AppContainerContext';
import AumAndFreshnessCard from './components/AumAndFreshnessCard';
import CoverageCard from './components/CoverageCard';
import ProviderChart from './components/ProviderChart';
import ProviderFeedStatusTable from './components/ProviderFeedStatusTable';
import useConnectedAssetsSummary from './hooks/useConnectedAssetsSummary';
import useConnectedAssetsProviders from './hooks/useConnectedAssetsProviders';
import CompanySectionLayout from 'src/company/CompanySectionLayout';

export default function ConnectedAssetsPage() {
  const navigate = useNavigate();
  const { setSidebarCollapsed } = useAppContainerContext();

  useEffect(() => {
    setSidebarCollapsed(true);
    return () => setSidebarCollapsed(false);
  }, [setSidebarCollapsed]);

  const {
    data: summary,
    isLoading: isSummaryLoading,
    isError: isSummaryError,
  } = useConnectedAssetsSummary();
  const {
    data: providers,
    isLoading: isProvidersLoading,
    isError: isProvidersError,
  } = useConnectedAssetsProviders();

  const isLoading = isSummaryLoading || isProvidersLoading;
  const isError = isSummaryError || isProvidersError;

  let content = null;

  if (isLoading) {
    content = <PageLoader absolute={false} />;
  } else if (isError) {
    content = (
      <Text c="red" size="lg">
        Failed to load connected assets data.
      </Text>
    );
  } else if (summary?.totalFeedProducts === 0) {
    content = (
      <Text c="dimmed" size="lg">
        Your feed is being configured. You will see your coverage here once it is active.
      </Text>
    );
  } else {
    content = (
      <Stack gap="lg">
        <Grid>
          <Grid.Col span={6}>
            <CoverageCard
              matchedProducts={summary?.matchedProducts ?? 0}
              totalFeedProducts={summary?.totalFeedProducts ?? 0}
              unmatchedProducts={summary?.unmatchedProducts ?? 0}
              coveragePercentage={summary?.coveragePercentage ?? 0}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <AumAndFreshnessCard
              connectedAum={summary?.connectedAum ?? 0}
              healthyProviderCount={
                providers?.providers.filter(p => p.health === 'HEALTHY').length ?? 0
              }
              totalProviderCount={providers?.providers.length ?? 0}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <ProviderChart providers={providers?.providers ?? []} />
          </Grid.Col>
        </Grid>
        <ProviderFeedStatusTable
          providers={providers?.providers ?? []}
          onMatchClick={provider => {
            const params = new URLSearchParams({
              provider: provider.provider,
              valuationsProvider: provider.valuationsProvider,
            });
            navigate(`/company/connected-assets/matching?${params}`);
          }}
        />
      </Stack>
    );
  }

  return <CompanySectionLayout title="Connected products">{content}</CompanySectionLayout>;
}
